import { useState } from 'react';
import { normalizeToolName, toolDisplayName, toolSummary } from '@/lib/ivyChat/toolNames';
import {
  ChevronRight,
  CheckCircle2, XCircle, Loader2, Code2,
  FileText, Terminal, Pencil, Search, Globe, Bot, Wrench,
  Layers, FolderOpen, Image, Upload, Mic, Film,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ToolFallbackProps {
  toolName: string;
  args: Record<string, unknown>;
  result?: unknown;
  argsText?: string;
  status: { type: string };
}

const SHOW_DEBUG = import.meta.env.VITE_SHOW_CHAT_DEBUG === 'true';

const TOOL_ICONS: Record<string, typeof Wrench> = {
  Read: FileText, Write: FileText, Edit: Pencil,
  Bash: Terminal, Glob: Search, Grep: Search,
  Agent: Bot, WebSearch: Globe, WebFetch: Globe,
  compile_slide: Layers, create_video_project: FolderOpen,
  generate_image: Image, sync_slide_to_production: Upload,
  generate_tts: Mic, build_video_manifest: Film,
};

function getIcon(name: string) {
  const norm = normalizeToolName(name);
  return TOOL_ICONS[norm] || TOOL_ICONS[name] || Wrench;
}

export function IvyToolFallback({ toolName, args, result, status }: ToolFallbackProps) {
  const [expanded, setExpanded] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  const Icon = getIcon(toolName);
  const displayName = toolDisplayName(toolName, args);
  const summary = toolSummary(toolName, args);
  const isRunning = status.type === 'running' || status.type === 'requires-action';
  const isError = status.type === 'error';
  const isComplete = status.type === 'complete';

  const headerInner = (
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
        {isRunning ? (
          <Loader2 className="w-3 h-3 text-info animate-spin" />
        ) : isError ? (
          <XCircle className="w-3 h-3 text-destructive" />
        ) : isComplete ? (
          <CheckCircle2 className="w-3 h-3 text-success" />
        ) : (
          <Icon className="w-3 h-3 text-muted-foreground" />
        )}
      </div>

      <span className="text-xs text-muted-foreground">
        {displayName}
      </span>

      <span className="text-xs text-muted-foreground/50 truncate flex-1">
        {summary}
      </span>

      {isRunning && (
        <div className="w-10 h-1 rounded-full animate-shimmer" />
      )}

      {SHOW_DEBUG && (
        <ChevronRight className={`w-3 h-3 text-muted-foreground/30 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      )}
    </div>
  );

  if (!SHOW_DEBUG) {
    return (
      <div className="animate-fade-in">
        <div className="w-full px-2 py-1 rounded-md">
          {headerInner}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'w-full text-left px-2 py-1 rounded-md transition-all duration-150',
          'hover:bg-muted/30',
          expanded ? 'bg-muted/20' : '',
        )}
      >
        {headerInner}
      </button>

      {expanded && (
        <div className="mt-1 ml-2 rounded-lg border border-border-secondary bg-surface-secondary/50 overflow-hidden">
          <div className="flex items-center justify-end px-3 py-1.5 border-b border-border-secondary">
            <button
              onClick={(e) => { e.stopPropagation(); setShowDebug(!showDebug); }}
              className={cn(
                'flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors',
                showDebug ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Code2 className="w-3 h-3" />
              Debug
            </button>
          </div>

          {showDebug ? (
            <div className="p-3 space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
              <div>
                <div className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-wider mb-1">Input</div>
                <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                  {JSON.stringify(args, null, 2)}
                </pre>
              </div>
              {result !== undefined && (
                <div>
                  <div className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-wider mb-1">Result</div>
                  <pre className={cn(
                    'text-xs font-mono whitespace-pre-wrap',
                    isError ? 'text-destructive' : 'text-muted-foreground',
                  )}>
                    {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 max-h-48 overflow-y-auto custom-scrollbar">
              <div className="text-xs text-muted-foreground space-y-1">
                {Object.entries(args).slice(0, 6).map(([key, value]) => {
                  const display = typeof value === 'string'
                    ? (value.length > 100 ? value.slice(0, 100) + '...' : value)
                    : JSON.stringify(value);
                  return (
                    <div key={key} className="flex gap-2">
                      <span className="font-mono text-muted-foreground/50 w-24 truncate text-right flex-shrink-0">{key}</span>
                      <span className="truncate">{display}</span>
                    </div>
                  );
                })}
              </div>
              {result !== undefined && !isRunning && (
                <div className="mt-2 pt-2 border-t border-border-secondary">
                  <div className={cn(
                    'text-xs',
                    isError ? 'text-destructive' : 'text-muted-foreground',
                  )}>
                    {String(typeof result === 'string' ? result : JSON.stringify(result)).slice(0, 200)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
